import {
  IMatch,
  IResponseShort,
  IBaseMatchData,
  TMatchData,
  IUpdateCheckoutDatePayload,
  TReview,
  TBaseReview,
  ISavedSearchPreference,
  ISearchPreferencePayload,
  PaymentStatusStripe,
  TAggregatedMatchesResponse,
  TStudentActionExtrasPayload
} from "interfaces/interfaces";
import { ServiceApi } from "../utils/utilAmplify";
import query from "../utils/query";
import { ServiceName } from "../config/amplify";
import { MatchStatusEnum } from "../utils/constants";

class StudentAPIClass {
  private readonly api: ServiceApi;

  constructor() {
    this.api = new ServiceApi(ServiceName.Protected);
  }

  getMatches(userId: string): Promise<TMatchData[]> {
    return this.api.get(`matches/student-matches/${userId}`);
  }
  getAggregatedMatches(
    userId: string,
    next_batch_token: string | null,
    from_match_status?: MatchStatusEnum,
    created_after?: Date | null
  ): Promise<TAggregatedMatchesResponse> {
    return this.api.get(
      `matches/student-matches-aggregated/${userId}?` +
        query.stringify({ next_batch_token, created_after, from_match_status })
    );
  }
  acceptInvite(payload: IBaseMatchData): Promise<IResponseShort> {
    return this.api.post("matches/accept-invite", payload);
  }
  rejectInvite(payload: IBaseMatchData): Promise<IResponseShort> {
    return this.api.post("matches/reject-invite", payload);
  }
  getMatch(
    studentId: string,
    hostId: string,
    houseId: string,
    options?: { showNotificationOnError?: boolean }
  ): Promise<IMatch> {
    return this.api
      .get(
        "matches/details?" +
          query.stringify({
            student_id: studentId,
            host_id: hostId,
            house_id: houseId
          }),
        options
      )
      .then((res) => res.data);
  }
  showInterest(
    payload: IBaseMatchData,
    message: string
  ): Promise<IResponseShort> {
    return this.api.post("matches/show-interest", {
      student_id: payload.student_id,
      host_id: payload.host_id,
      house_id: payload.house_id,
      message
    });
  }
  updateCheckoutDate(
    payload: IUpdateCheckoutDatePayload
  ): Promise<IResponseShort> {
    return this.api.post("matches/change-checkout-date", payload);
  }
  setActions(payload: TStudentActionExtrasPayload): Promise<IResponseShort> {
    return this.api.post("matches/set-student-actions", payload);
  }

  // Reviews:
  createReview(
    payload: Pick<TReview, "rating" | "review_text" | "house_id">,
    host_id: string
  ): Promise<TReview> {
    return this.api.post("reviews/" + host_id, payload);
  }
  updateReview(
    payload: Partial<TBaseReview> & { created_at: string },
    host_id: string
  ): Promise<IResponseShort> {
    return this.api.patch("reviews/" + host_id, payload);
  }

  // Searches:
  getSearches(userId: string): Promise<ISavedSearchPreference[]> {
    return this.api.get(`users/student/${userId}/search-preference`);
  }
  saveSearchPreference(
    payload: ISearchPreferencePayload,
    userId?: string
  ): Promise<ISavedSearchPreference> {
    return this.api.post(`users/student/${userId}/search-preference`, payload);
  }
  updateSearchPreference(
    created_at: string,
    payload: Partial<ISearchPreferencePayload>,
    userId?: string
  ): Promise<ISavedSearchPreference> {
    return this.api.patch(
      `users/student/${userId}/search-preference?` +
        query.stringify({ created_at }),
      payload
    );
  }
  deleteSearchPreference(
    created_at: string,
    userId?: string
  ): Promise<IResponseShort> {
    return this.api.del(
      `users/student/${userId}/search-preference?` +
        query.stringify({ created_at })
    );
  }

  // Stripe payments for Premium account:
  createPremiumPaymentIntent(): Promise<{ client_secret: string }> {
    return this.api.post(`premium-payments/create-intent`);
  }
  getPaymentStatus(
    paymentId: string
  ): Promise<{ status: PaymentStatusStripe }> {
    return this.api.get(`premium-payments/get-status/${paymentId}`);
  }

  // Stripe deposit payments:
  createDepositPaymentIntent(
    payload: IBaseMatchData
  ): Promise<{ client_secret: string }> {
    return this.api.post(`deposit-payments/create-intent`, payload);
  }
  getDepositPaymentStatus(
    paymentId: string
  ): Promise<{ status: PaymentStatusStripe }> {
    return this.api.get(`deposit-payments/get-status/${paymentId}`);
  }
}

export const StudentAPI = new StudentAPIClass();
